'use client';
import { useState, useEffect } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { contacts, getInTouchDetailsScreen, supportLink } from '@/_config';

const STAR_COLOR = '#f59e0b';

const MiniStars = ({ rating = 0 }) => {
    const filled = Math.round(rating);
    return (
        <div style={{ display: 'flex', gap: '2px' }}>
            {[1, 2, 3, 4, 5].map(s => (
                <i key={s}
                    className={`pi ${s <= filled ? 'pi-star-fill' : 'pi-star'}`}
                    style={{ fontSize: '0.65rem', color: s <= filled ? STAR_COLOR : '#d1d5db' }}
                />
            ))}
        </div>
    );
};

export const WarrantySection = ({ productData, storeConfig }) => {
    const [open,     setOpen]     = useState(false);
    const [mounted,  setMounted]  = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        setMounted(true);
        const check = () => setIsMobile(window.innerWidth < 640);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    const warranty = productData?.warranty;
    const { duration, time, info } = warranty || {};
    const hasWarranty = duration && Number(duration) > 0;
    const replacementDays = storeConfig?.replacementAllow ?? 7;
    const coverImage = productData?.coverImage;
    const productName = productData?.title;
    const avgRating = productData?.averageRating || 0;
    const reviewCount = productData?.reviewCount || 0;
    const OutOfWarranty = productData?.OutOfWarranty;
    const app_supported = productData?.app_supported || [];
    const Work_With = productData?.Work_With || [];

    const highlights = [
        {
            icon: 'pi-shield',
            label: hasWarranty ? `${duration} ${time} Warranty` : 'No Warranty',
            sub: hasWarranty ? (info || 'Manufacturer warranty') : 'Not covered',
            color: hasWarranty ? 'var(--primary-color)' : '#94a3b8',
            bgColor: hasWarranty ? 'rgba(11,49,99,0.06)' : '#f8fafc',
            borderColor: hasWarranty ? 'rgba(11,49,99,0.15)' : '#e2e8f0',
        },
        {
            icon: 'pi-sync',
            label: `${replacementDays}-Day Replacement`,
            sub: 'Defective product',
            color: 'var(--secondary-color)',
            bgColor: 'rgba(27,184,167,0.06)',
            borderColor: 'rgba(27,184,167,0.2)',
        },
        {
            img: '/icon-logo.png',
            label: 'Brand Assured',
            sub: 'Quality tested',
            color: 'var(--primary-color)',
            bgColor: 'rgba(11,49,99,0.06)',
            borderColor: 'rgba(11,49,99,0.15)',
        },
        {
            icon: 'pi-truck',
            label: 'Transit Safe',
            sub: 'Report within 48h',
            color: 'var(--secondary-color)',
            bgColor: 'rgba(27,184,167,0.06)',
            borderColor: 'rgba(27,184,167,0.2)',
        },
    ];

    return (
        <>
            {/* ── Warranty sidebar ──────────────────────────── */}
            {mounted && (
                <Sidebar
                    visible={open}
                    onHide={() => setOpen(false)}
                    position="right"
                    style={{ width: '440px', maxWidth: '96vw' }}
                    header={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                width: '28px', height: '28px', borderRadius: '8px',
                                background: 'var(--primary-color)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <i className="pi pi-shield" style={{ fontSize: '0.75rem', color: '#fff' }} />
                            </div>
                            <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#1e293b' }}>
                                Warranty & Coverage
                            </span>
                        </div>
                    }
                    pt={{
                        header: { style: { background: 'var(--surface-100, #f8fafc)', borderBottom: '1px solid var(--surface-200, #e2e8f0)', padding: '12px 16px' } },
                        content: { style: { padding: 0 } },
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>

                        {/* Product hero */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '14px',
                            padding: '14px 16px',
                            background: 'linear-gradient(135deg, #f0fdfa 0%, #fff 100%)',
                            borderBottom: '1px solid #e2e8f0',
                        }}>
                            {coverImage ? (
                                <div style={{
                                    width: '64px', height: '64px', borderRadius: '12px',
                                    background: '#fff', border: '1.5px solid rgba(11,49,99,0.2)',
                                    flexShrink: 0, overflow: 'hidden', padding: '4px',
                                    boxShadow: '0 4px 12px rgba(11,49,99,0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img src={coverImage} alt={productName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                </div>
                            ) : (
                                <div style={{
                                    width: '64px', height: '64px', borderRadius: '12px',
                                    background: 'var(--primary-color)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    boxShadow: '0 4px 12px rgba(11,49,99,0.22)',
                                }}>
                                    <i className="pi pi-box" style={{ color: '#fff', fontSize: '1.5rem' }} />
                                </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{
                                    margin: '0 0 7px', fontWeight: 700, fontSize: '0.85rem',
                                    color: 'var(--primary-color)',
                                    display: '-webkit-box', WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                }}>
                                    {productName}
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    {avgRating > 0 && (
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                                            background: 'rgba(27,184,167,0.1)', color: 'var(--secondary-color)',
                                            border: '1px solid rgba(27,184,167,0.3)', borderRadius: '20px',
                                            padding: '3px 10px', fontSize: '0.7rem', fontWeight: 800,
                                        }}>
                                            <MiniStars rating={avgRating} />
                                            {Number(avgRating).toFixed(1)}
                                        </span>
                                    )}
                                    {reviewCount > 0 && (
                                        <span style={{
                                            background: '#f1f5f9', color: '#64748b',
                                            border: '1px solid #e2e8f0', borderRadius: '20px',
                                            padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600,
                                        }}>
                                            {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Warranty details */}
                        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>

                            {/* Main warranty */}
                            <div style={{
                                padding: '14px 16px', borderRadius: '12px',
                                border: `1px solid ${hasWarranty ? 'rgba(11,49,99,0.2)' : '#e2e8f0'}`,
                                background: hasWarranty ? 'rgba(11,49,99,0.03)' : '#f8fafc',
                                display: 'flex', alignItems: 'flex-start', gap: '12px',
                            }}>
                                <div style={{
                                    width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                                    background: hasWarranty ? 'rgba(11,49,99,0.08)' : '#f1f5f9',
                                    border: `1px solid ${hasWarranty ? 'rgba(11,49,99,0.15)' : '#e2e8f0'}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <i className={`pi ${hasWarranty ? 'pi-shield' : 'pi-times-circle'}`}
                                        style={{ color: hasWarranty ? 'var(--primary-color)' : '#94a3b8', fontSize: '1.1rem' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p style={{ margin: '0 0 3px', fontWeight: 700, fontSize: '0.9rem', color: hasWarranty ? 'var(--primary-color)' : '#64748b' }}>
                                        {hasWarranty ? `${duration} ${time} Manufacturer Warranty` : 'No Warranty'}
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.76rem', color: '#64748b', lineHeight: 1.5 }}>
                                        {hasWarranty ? (info || `This product includes a ${duration} ${time} manufacturer warranty covering manufacturing defects.`) : "This product does not include a manufacturer warranty."}
                                    </p>
                                </div>
                            </div>

                            {/* Replacement */}
                            <div style={{
                                padding: '14px 16px', borderRadius: '12px',
                                border: '1px solid rgba(27,184,167,0.2)',
                                background: 'rgba(27,184,167,0.03)',
                                display: 'flex', alignItems: 'flex-start', gap: '12px',
                            }}>
                                <div style={{
                                    width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                                    background: 'rgba(27,184,167,0.08)',
                                    border: '1px solid rgba(27,184,167,0.2)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <i className="pi pi-sync" style={{ color: 'var(--secondary-color)', fontSize: '1.1rem' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p style={{ margin: '0 0 3px', fontWeight: 700, fontSize: '0.9rem', color: 'var(--primary-color)' }}>
                                        {replacementDays}-Day Replacement Guarantee
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.76rem', color: '#64748b', lineHeight: 1.5 }}>
                                        If you receive a defective or damaged product, request a replacement within {replacementDays} days of delivery.
                                    </p>
                                </div>
                            </div>

                            {/* Transit */}
                            <div style={{
                                padding: '14px 16px', borderRadius: '12px',
                                border: '1px solid rgba(11,49,99,0.15)',
                                background: 'rgba(11,49,99,0.02)',
                                display: 'flex', alignItems: 'flex-start', gap: '12px',
                            }}>
                                <div style={{
                                    width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                                    background: 'rgba(11,49,99,0.06)',
                                    border: '1px solid rgba(11,49,99,0.12)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <i className="pi pi-truck" style={{ color: 'var(--primary-color)', fontSize: '1.1rem' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p style={{ margin: '0 0 3px', fontWeight: 700, fontSize: '0.9rem', color: 'var(--primary-color)' }}>
                                        Transit Safe
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.76rem', color: '#64748b', lineHeight: 1.5 }}>
                                        Damaged in transit? Report within 48 hours with photos. We will replace at no extra cost.
                                    </p>
                                </div>
                            </div>

                            {/* Out of warranty */}
                            {OutOfWarranty && (
                                <div style={{
                                    padding: '14px 16px', borderRadius: '12px',
                                    border: '1px solid #e2e8f0', background: '#f8fafc',
                                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                                }}>
                                    <div style={{
                                        width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                                        background: '#f1f5f9', border: '1px solid #e2e8f0',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <i className="pi pi-wrench" style={{ color: '#64748b', fontSize: '1.1rem' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <p style={{ margin: '0 0 3px', fontWeight: 700, fontSize: '0.9rem', color: '#475569' }}>
                                            Out-of-Warranty Repair
                                        </p>
                                        <p style={{ margin: 0, fontSize: '0.76rem', color: '#64748b', lineHeight: 1.5 }}>
                                            {OutOfWarranty?.title}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* App supported */}
                            {app_supported.length > 0 && (
                                <div>
                                    <p style={{ margin: '8px 0', fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary-color)' }}>
                                        App Supported
                                    </p>
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        {app_supported.map((app, i) => (
                                            <div key={i} style={{
                                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px',
                                                padding: '10px 14px', borderRadius: '10px',
                                                border: '1px solid rgba(11,49,99,0.15)',
                                                background: '#fff', minWidth: '80px',
                                            }}>
                                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                                <img src={app?.logo} alt={app?.title} height="40px" />
                                                <span style={{ fontSize: '0.7rem', color: '#475569', fontWeight: 500 }}>{app?.title}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Works with */}
                            {Work_With.length > 0 && (
                                <div>
                                    <p style={{ margin: '8px 0', fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary-color)' }}>
                                        Works With
                                    </p>
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        {Work_With.map((app, i) => (
                                            <div key={i} style={{
                                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px',
                                                padding: '10px 14px', borderRadius: '10px',
                                                border: '1px solid rgba(11,49,99,0.15)',
                                                background: '#fff', minWidth: '80px',
                                            }}>
                                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                                <img src={app?.logo} alt={app?.title} height="35px" />
                                                <span style={{ fontSize: '0.7rem', color: '#475569', fontWeight: 500 }}>{app?.title}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Support */}
                            <div style={{
                                padding: '14px 16px', borderRadius: '12px',
                                border: '1px solid rgba(27,184,167,0.2)',
                                background: 'rgba(27,184,167,0.03)',
                                marginTop: '4px',
                            }}>
                                <p style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary-color)' }}>
                                    Need Help?
                                </p>
                                <p style={{ margin: '0 0 10px', fontSize: '0.76rem', color: '#64748b', lineHeight: 1.5 }}>{getInTouchDetailsScreen}</p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
                                    <a href={`tel:${contacts?.support}`} style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        fontSize: '0.78rem', color: 'var(--primary-color)', fontWeight: 600,
                                        textDecoration: 'none',
                                    }}>
                                        <i className="pi pi-phone" style={{ color: 'var(--secondary-color)', fontSize: '0.75rem' }} />
                                        {contacts?.support}
                                    </a>
                                    <a href={`mailto:${contacts?.emailSupport}`} style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        fontSize: '0.78rem', color: 'var(--primary-color)', fontWeight: 600,
                                        textDecoration: 'none',
                                    }}>
                                        <i className="pi pi-envelope" style={{ color: 'var(--secondary-color)', fontSize: '0.75rem' }} />
                                        {contacts?.emailSupport}
                                    </a>
                                </div>
                                <a href={supportLink} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    background: 'var(--primary-color)', color: '#fff',
                                    borderRadius: '8px', padding: '8px 16px',
                                    fontSize: '0.78rem', fontWeight: 700,
                                    textDecoration: 'none', cursor: 'pointer',
                                }}>
                                    <i className="pi pi-headphones" style={{ fontSize: '0.75rem' }} />
                                    Contact Support
                                    <i className="pi pi-arrow-right" style={{ fontSize: '0.6rem' }} />
                                </a>
                            </div>
                        </div>
                    </div>
                </Sidebar>
            )}

            {/* ── Warranty card (trigger) ────────────────────── */}
            <div
                onClick={() => mounted && setOpen(true)}
                style={{
                    display: 'flex', alignItems: 'stretch', gap: '0',
                    borderRadius: '12px',
                    border: '1px solid rgba(11,49,99,0.15)',
                    background: '#fff',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s, border-color 0.2s',
                    boxShadow: '0 1px 4px rgba(11,49,99,0.06)',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(11,49,99,0.12)'; e.currentTarget.style.borderColor = 'rgba(11,49,99,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(11,49,99,0.06)'; e.currentTarget.style.borderColor = 'rgba(11,49,99,0.15)'; }}
            >
                {highlights.map((item, i) => (
                    <div key={i} style={{
                        flex: 1, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        gap: isMobile ? '4px' : '6px',
                        padding: isMobile ? '10px 4px' : '14px 8px',
                        borderRight: i < highlights.length - 1 ? '1px solid rgba(11,49,99,0.08)' : 'none',
                        background: '#fff',
                    }}>
                        <div style={{
                            width: isMobile ? '28px' : '36px',
                            height: isMobile ? '28px' : '36px',
                            borderRadius: '10px',
                            background: item.bgColor,
                            border: `1px solid ${item.borderColor}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                            overflow: 'hidden',
                        }}>
                            {item.img ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img src={item.img} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '3px' }} />
                            ) : (
                                <i className={`pi ${item.icon}`} style={{ color: item.color, fontSize: isMobile ? '0.75rem' : '0.95rem' }} />
                            )}
                        </div>
                        <span style={{
                            fontSize: isMobile ? '0.58rem' : '0.68rem', fontWeight: 700,
                            color: item.color, textAlign: 'center', lineHeight: 1.3,
                        }}>
                            {item.label}
                        </span>
                        {!isMobile && (
                            <span style={{ fontSize: '0.6rem', color: '#94a3b8', textAlign: 'center', lineHeight: 1.2 }}>
                                {item.sub}
                            </span>
                        )}
                    </div>
                ))}

                {/* "View details" chevron */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 12px',
                    borderLeft: '1px solid rgba(11,49,99,0.08)',
                    color: 'var(--secondary-color)',
                }}>
                    <i className="pi pi-angle-right" style={{ fontSize: '1rem' }} />
                </div>
            </div>
        </>
    );
};
